import { bind } from '@react-rxjs/core'
import { createSignal } from '@react-rxjs/utils'
import { startWith, switchMap } from 'rxjs'

import { IndexedProduct } from '../../../graphql/search'
import { right } from '../../../utils/result'
import { mapRight, switchMapRight } from '../../../utils/rx/operators'
import { searchAutoCompleteQuery } from '../../search/autocomplete-query'
import { searchExactQuery } from '../../search/exact-query'
import { dispensariesSiteSettingsCdn$ } from '../query'
import { mainProductList } from '../utils'
import { sanityDispensaryGetOrCreate$ } from './dispensary'

export type Product = Pick<
  IndexedProduct,
  'id' | 'name' | 'urlKey' | 'shortDescription' | 'sanityImageJson' | 'sku'
>

export const addProductToList = (productToAdd: Product) => (products?: Product[]) => {
  if (!products) {
    return [productToAdd]
  }
  const i = products.findIndex((product) => product.sku === productToAdd.sku)
  if (i !== -1) {
    return products
  }
  return [productToAdd, ...products]
}

export const moveProductUpList = (productToMove: Product) => (products?: Product[]) => {
  if (!products) {
    return [productToMove]
  }
  const i = products.findIndex((product) => product.sku === productToMove.sku)
  if (i === -1 || i === 0) {
    return products
  }
  return [...products.slice(0, i - 1), products[i], products[i - 1], ...products.slice(i + 1)]
}

export const moveProductDownList = (productToMove: Product) => (products?: Product[]) => {
  if (!products) {
    return [productToMove]
  }
  const i = products.findIndex((product) => product.sku === productToMove.sku)
  if (i === -1 || i === products.length - 1) {
    return products
  }
  const i2 = i + 1 // swapping i + 1 up is the same as swapping i down
  return [...products.slice(0, i2 - 1), products[i2], products[i2 - 1], ...products.slice(i2 + 1)]
}

export const removeProductFromList = (productToRemove: Product) => (products?: Product[]) => {
  if (!products) {
    return []
  }
  const i = products.findIndex((product) => product.sku === productToRemove.sku)
  if (i === -1) {
    return products
  }
  return [...products.slice(0, i), ...products.slice(i + 1)]
}

const sanityManageFeaturedProducts$ = sanityDispensaryGetOrCreate$.pipe(
  mapRight(({ data }) => ({
    _tag: 'Right' as const,
    data: mainProductList(data.productLists),
  })),
  mapRight(({ data }) => ({
    _tag: 'Right' as const,
    data: { productList: data, skus: data?.skus ?? [] },
  })),
  switchMapRight(({ data: { productList, skus } }) =>
    searchExactQuery({ skus }).pipe(
      mapRight((value) =>
        right({
          loading: false,
          productList,
          products: value.data,
          unavailableSkus: skus.filter((sku) => !value.data.some((product) => product.sku === sku)),
        }),
      ),
      startWith(right({ loading: true, productList, products: [], unavailableSkus: [] })),
    ),
  ),
)
export const [useSanityManageFeaturedProducts] = bind(sanityManageFeaturedProducts$)

const suggestedFeaturedProducts$ = dispensariesSiteSettingsCdn$.pipe(
  mapRight((siteSettingsResult) => ({
    _tag: 'Right' as const,
    data: {
      defaultSkus:
        mainProductList(siteSettingsResult.data?.defaultDispensaryProductLists)?.skus || [],
    },
  })),
  switchMapRight(({ data: { defaultSkus } }) => searchExactQuery({ skus: defaultSkus })),
)
export const [useSuggestedFeaturedProducts, latestSuggestedFeaturedProducts$] = bind(
  suggestedFeaturedProducts$,
)

const [searchFeaturedProductsAutoComplete$, searchFeaturedProductsAutoComplete] = createSignal<{
  query?: string
}>()
export { searchFeaturedProductsAutoComplete }
const featuredProductsAutoComplete$ = searchFeaturedProductsAutoComplete$.pipe(
  switchMap(({ query }) =>
    searchAutoCompleteQuery({ query }).pipe(
      mapRight((value) => right({ loading: false, products: value.data })),
      startWith(right({ loading: true, products: [] })),
    ),
  ),
)
export const [useFeaturedProductsAutoComplete, latestFeaturedProductsAutoComplete$] = bind(
  featuredProductsAutoComplete$,
  { _tag: 'Right', data: { loading: false, products: [] } },
)
